import { IUser } from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { getDisplayName } from '../../../../models/NameUtils';
import { UserAutocomplete } from '../../../../web/components/UserAutocomplete';
import { ExportConfirmationModalV2 } from '../../../../web/components/ExportConfirmation';
import { Button } from '../../../../web/components-v2/Button';
import { GoalDatePicker } from '../../../components/goals/GoalDatePicker';
import { GoalsTable } from '../../../components/goals/GoalsTable';
import { useErrorMessages } from '../../../hooks';
import { useGoalsExportMutation } from '../../../queries/Goals';
import { GoalsViewModel } from '../../../viewModels/goals';
import { BasicPage } from '../../BasicPage';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
}

export const Goals: React.FC<IProps> = observer(({ className = '' }) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const goals = useRef(new GoalsViewModel(userSession)).current;
	const exportMutation = useGoalsExportMutation({
		onError: err => errorMessages.pushApiError(err),
	});

	useEffect(() => {
		goals.reset();

		goals.load()?.catch(err => errorMessages.pushApiError(err));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [goals.user]);

	const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.currentTarget.files[0];
		if (!file) {
			return; // cancelled the file picker
		}

		inputRef.current.value = '';
		goals
			.upload(file)

			.then(() => toast.success('Goals were uploaded'))

			.catch(err => errorMessages.pushApiError(err));
	};

	const onUploadClick = () => {
		inputRef.current?.click();
	};

	// The modal takes it from here: it polls the job and offers the file for download when it finishes.
	const onExportClick = () => exportMutation.mutate(goals.request);

	const onUserSelected = (user: IUser | null) => {
		if (!user) {
			return;
		}
		goals.user = user;
	};

	const onUserClear = (): void => (goals.user = null);

	return (
		<BasicPage className={`${css(styleSheet.goalsContainer)} ${className}`} appearance='light' title='Goals'>
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.header)}>
					<div className={css(styleSheet.userSearch)}>
						<UserAutocomplete
							ariaLabel='Search for a user'
							id='user-select-goals'
							onChange={onUserSelected}
							placeholder='Search for a user...'
							value={null}
						/>
					</div>
					<div className={css(styleSheet.selectedUser)}>{getDisplayName(goals.user)}</div>
					{goals.user && (
						<Button variant='primary' onClick={onUserClear}>
							Clear
						</Button>
					)}
				</div>
				<div className={css(styleSheet.header)}>
					<span className={css(styleSheet.label)}>Date Range:</span>
					<GoalDatePicker goals={goals} />
					<div className={css(styleSheet.buttonGroup)}>
						<input
							type='file'
							className={css(styleSheet.fileInput)}
							onChange={onFileChange}
							ref={inputRef}
							accept='.xlsx,.csv'
						/>
						<Button variant='primary' onClick={onUploadClick}>
							Upload
						</Button>
						<Button variant='primary' onClick={onExportClick} isLoading={exportMutation.isPending}>
							Export
						</Button>
					</div>
				</div>
			</div>
			<GoalsTable goals={goals} />
			{exportMutation.isIdle ? null : (
				<ExportConfirmationModalV2
					mutationResult={exportMutation}
					modalProps={{
						isOpen: !exportMutation.isIdle,
						onRequestClose: () => exportMutation.reset(),
					}}
				/>
			)}
		</BasicPage>
	);
});
